import React, { useRef, useState } from 'react';
import { adaAlert, adaSwal } from '../utils/adaSwal';
import {
	suggestTargetingFromDocument,
	suggestTargetingFromUrl,
} from '../api/aiSuggestApi';

const TARGETING_FIELDS = [
	'malGeo',
	'geoAge',
	'wishesDemands',
	'skillsTitles',
	'companies',
	'industryInterests',
	'hybridHome',
];

const DROPZONE_TEXT =
	'Last opp stillingsannonsen som Word (.docx) eller PDF. Dette gir deg forslag i feltene under. Du kan justere etterpå før insending. Klikk her for å laste opp, eller dra dokumentet inn i denne boksen.';

const LINK_HINT =
	'Lim inn lenke til stillingsannonsen. Vi henter teksten og lager forslag i feltene under. Du kan justere etterpå før innsending.';

function fieldHasValue(name, value) {
	if (name === 'hybridHome') {
		return Boolean(value);
	}
	return String(value || '').trim().length > 0;
}

function isSupportedDocument(file) {
	const name = String(file?.name || '').toLowerCase();
	return name.endsWith('.docx') || name.endsWith('.pdf');
}

function normalizeUrlInput(value) {
	const trimmed = String(value || '').trim();
	if (!trimmed) {
		return '';
	}
	if (/^https?:\/\//i.test(trimmed)) {
		return trimmed;
	}
	if (trimmed.includes('.') && !/\s/.test(trimmed)) {
		return `https://${trimmed}`;
	}
	return trimmed;
}

function isHttpUrl(value) {
	try {
		const parsed = new URL(normalizeUrlInput(value));
		return parsed.protocol === 'http:' || parsed.protocol === 'https:';
	} catch {
		return false;
	}
}

const AiTargetingSuggest = ({ setValue, getValues, onInferredChange }) => {
	const inputRef = useRef(null);
	const urlInputRef = useRef(null);
	const [source, setSource] = useState('document');
	const [file, setFile] = useState(null);
	const [url, setUrl] = useState('');
	const [loading, setLoading] = useState(false);
	const [isDragging, setIsDragging] = useState(false);

	const canGenerate =
		source === 'document' ? Boolean(file) : isHttpUrl(url);

	const acceptFile = (next) => {
		if (!next) {
			return;
		}
		if (!isSupportedDocument(next)) {
			void adaAlert(
				'Kun Word (.docx) og PDF støttes. Lagre dokumentet som .docx eller .pdf og prøv igjen.'
			);
			if (inputRef.current) {
				inputRef.current.value = '';
			}
			return;
		}
		setFile(next);
		setSource('document');
	};

	const handleFileChange = (event) => {
		acceptFile(event.target.files?.[0] || null);
	};

	const handleDragOver = (event) => {
		event.preventDefault();
		if (loading || source !== 'document') {
			return;
		}
		setIsDragging(true);
	};

	const handleDragLeave = (event) => {
		if (event.currentTarget.contains(event.relatedTarget)) {
			return;
		}
		setIsDragging(false);
	};

	const handleDrop = (event) => {
		event.preventDefault();
		setIsDragging(false);
		if (loading) {
			return;
		}
		acceptFile(event.dataTransfer.files?.[0] || null);
	};

	const applyFields = (fields, inferred) => {
		if (!fields || typeof fields !== 'object') {
			return;
		}
		for (const name of TARGETING_FIELDS) {
			if (!(name in fields)) {
				continue;
			}
			if (name === 'hybridHome') {
				setValue(name, Boolean(fields[name]), {
					shouldDirty: true,
					shouldTouch: true,
				});
				continue;
			}
			if (typeof fields[name] !== 'string') {
				continue;
			}
			setValue(name, fields[name], {
				shouldDirty: true,
				shouldTouch: true,
			});
		}
		if (typeof onInferredChange === 'function') {
			onInferredChange(
				inferred && typeof inferred === 'object' ? inferred : {}
			);
		}
	};

	const handleSuggest = async () => {
		if (!canGenerate || loading) {
			return;
		}

		const hasExisting = TARGETING_FIELDS.some((name) =>
			fieldHasValue(name, getValues(name))
		);
		if (hasExisting) {
			const confirm = await adaSwal({
				icon: 'question',
				title: 'Overskrive felt?',
				text: 'Noen målrettingsfelt har allerede innhold. Vil du erstatte dem med AI-forslag?',
				showCancelButton: true,
				confirmButtonText: 'Overskriv',
				cancelButtonText: 'Avbryt',
			});
			if (!confirm.isConfirmed) {
				return;
			}
		}

		setLoading(true);
		try {
			const data =
				source === 'link'
					? await suggestTargetingFromUrl(normalizeUrlInput(url))
					: await suggestTargetingFromDocument(file);
			console.log('[AI suggest] backend-respons', data);
			console.log(
				'[AI suggest] companies (hvor_kandidaten_jobber)',
				data?.fields?.companies
			);
			applyFields(data?.fields, data?.inferred);
			await adaSwal({
				icon: 'success',
				title: 'Forslag fylt inn',
				text: 'Du kan redigere feltene før du sender inn bestillingen.',
				confirmButtonText: 'OK',
			});
		} catch (error) {
			const message =
				error.response?.data?.error ||
				error.message ||
				'AI utilgjengelig.';
			await adaAlert(message, 'Kunne ikke hente forslag');
		} finally {
			setLoading(false);
		}
	};

	const openFilePicker = () => {
		if (loading || source !== 'document') {
			return;
		}
		inputRef.current?.click();
	};

	const dropzoneClassName = [
		'ai-targeting-suggest__dropzone',
		source === 'link' ? 'is-link-mode' : '',
		isDragging ? 'is-dragging' : '',
		loading ? 'is-loading' : '',
		source === 'document' && file ? 'has-file' : '',
		source === 'link' && isHttpUrl(url) ? 'has-file' : '',
	]
		.filter(Boolean)
		.join(' ');

	return (
		<div className='ai-targeting-suggest'>
			<h2>Målretting</h2>
			<div className='ai-targeting-suggest__toolbar'>
				<div className='ai-targeting-suggest__modes' role='tablist'>
					<button
						type='button'
						role='tab'
						aria-selected={source === 'document'}
						className={
							source === 'document'
								? 'ai-targeting-suggest__mode is-active'
								: 'ai-targeting-suggest__mode'
						}
						onClick={() => setSource('document')}
						disabled={loading}
					>
						Last opp dokument
					</button>
					<button
						type='button'
						role='tab'
						aria-selected={source === 'link'}
						className={
							source === 'link'
								? 'ai-targeting-suggest__mode is-active'
								: 'ai-targeting-suggest__mode'
						}
						onClick={() => setSource('link')}
						disabled={loading}
					>
						Lim inn lenke
					</button>
				</div>
				<button
					type='button'
					className='ai-targeting-suggest__submit'
					onClick={handleSuggest}
					disabled={!canGenerate || loading}
				>
					{loading ? 'Genererer forslag...' : 'Generer forslag'}
				</button>
			</div>
			<div
				className={dropzoneClassName}
				role={source === 'document' ? 'button' : undefined}
				tabIndex={loading || source === 'link' ? -1 : 0}
				aria-busy={loading}
				aria-label={source === 'link' ? LINK_HINT : DROPZONE_TEXT}
				onClick={source === 'document' ? openFilePicker : undefined}
				onKeyDown={(event) => {
					if (source !== 'document') {
						return;
					}
					if (event.key === 'Enter' || event.key === ' ') {
						event.preventDefault();
						openFilePicker();
					}
				}}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				onDrop={handleDrop}
			>
				<input
					ref={inputRef}
					className='ai-targeting-suggest__file-input'
					type='file'
					accept='.docx,.pdf,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document'
					onChange={handleFileChange}
					disabled={loading}
					tabIndex={-1}
				/>
				{loading ? (
					<div className='ai-targeting-suggest__loader'>
						<span
							className='ai-targeting-suggest__spinner'
							aria-hidden='true'
						/>
						<span>
							{source === 'link'
								? 'Henter siden og genererer forslag…'
								: 'Genererer forslag…'}
						</span>
					</div>
				) : source === 'link' ? (
					<div className='ai-targeting-suggest__link'>
						<span className='ai-targeting-suggest__link-hint'>
							{LINK_HINT}
						</span>
						<input
							ref={urlInputRef}
							className='ai-targeting-suggest__url-input'
							type='url'
							inputMode='url'
							autoComplete='url'
							placeholder='https://www.firma.no/stilling'
							value={url}
							disabled={loading}
							onClick={(event) => event.stopPropagation()}
							onChange={(event) => setUrl(event.target.value)}
							onKeyDown={(event) => {
								if (event.key === 'Enter') {
									event.preventDefault();
									void handleSuggest();
								}
							}}
						/>
					</div>
				) : (
					<span className='ai-targeting-suggest__dropzone-text'>
						{DROPZONE_TEXT}
					</span>
				)}
			</div>
		</div>
	);
};

export default AiTargetingSuggest;
